import * as React from "react";
import { useEffect, useState, useContext } from "react";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import { Dimensions, StyleSheet } from "react-native";
import { Text, View } from "../../../components/shared/Themed";
import Colors from "../../../utils/constants/colors";
import useColorScheme from "../../../hooks/useColorScheme";
import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";
import ReportsDrawerNavigator from "../DrawerNavigation/ReportsDrawerNavigator";
import DeliveryDrawerNavigator from "./../Delivery/DeliveryDrawerNavigator";
import { HomeTabParamList } from "../../../types";
import ContainersDrawerNavigator from "../../StandardUserNavigator/Containers/ContainersDrawerNavigator";
import { useSelector } from "react-redux";
import TabBarIcon from "../../../components/core/TabBarIcon";
import ScansDrawerNavigator from "../Scans/ScansDrawerNavigator";
import Spinner from "../../../components/shared/Spinner";
import OperationsDrawerNavigator from "../Operation/OperationsDrawerNavigator";
import ChinaTrackerDrawerNavigator from "../ChinaTracker/ChinaTrackerDrawerNavigator";
import teamsConfig from "../../../utils/constants/teams";
import rolesConfig from "../../../utils/constants/roles";
import ProductTrackerDrawerNavigator from "../ProductTracker/ProductTrackerDrawerNavigator";
import PortalOrdersDrawerNavigator from "../PortalOrders/PortalOrdersDrawerNavigator";
import { useTranslation } from "../../../utils/useTranslation";

const Tab = createMaterialTopTabNavigator<HomeTabParamList>();

export default function HomeTabNavigator() {
  const colorScheme = useColorScheme();
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const { t } = useTranslation();
  const { isMobileDevice: isPhone } = useContext(DeviceOrientationContext);
  const { userInfo, currentRole } = useSelector(
    (state: any) => state.configurations,
  );

  const userTeam = userInfo?.team?.toUpperCase();
  const isAdminOrOwner = [
    rolesConfig.roles.admin,
    rolesConfig.roles.owner,
  ].includes(currentRole?.toUpperCase());
  const canAccessDefaultTabs =
    [
      teamsConfig.teams.purchasing,
      teamsConfig.teams.operation,
      teamsConfig.teams.sales,
      teamsConfig.teams.admin,
      teamsConfig.teams.manager,
      teamsConfig.teams.salesManager,
    ].includes(userTeam) || isAdminOrOwner;
  const isChinaUser = userTeam === teamsConfig.teams.china && !isAdminOrOwner;

  const [hasTabsToShow, setHasTabsToShow] = useState<boolean>(false);

  const tabScreens: any = [];

  if (canAccessDefaultTabs) {
    tabScreens.push(
      {
        name: "Reports",
        component: ReportsDrawerNavigator,
        icon: "archive",
        label: t("reports"),
      },
      {
        name: "Containers",
        component: ContainersDrawerNavigator,
        icon: "ship",
        label: t("containers"),
      },
    );

    tabScreens.push({
      name: "Tracker",
      component: ProductTrackerDrawerNavigator,
      icon: "calendar-o",
      label: t("tracker"),
    });

    if (
      [teamsConfig.teams.sales, teamsConfig.teams.operation].includes(
        userTeam,
      ) ||
      isAdminOrOwner
    ) {
      tabScreens.push({
        name: "Customer",
        component: PortalOrdersDrawerNavigator,
        icon: "male",
        label: t("customer"),
      });
    }

    if (process.env.NODE_ENV !== "production") {
      tabScreens.push({
        name: "Operations",
        component: OperationsDrawerNavigator,
        icon: "tasks",
        label: t("operations"),
      });
    }

    tabScreens.push({
      name: "Delivery",
      component: DeliveryDrawerNavigator,
      icon: "truck",
      label: t("orderDelivery"),
    });
  }

  if (isChinaUser) {
    tabScreens.push({
      name: "Tracker",
      component: ChinaTrackerDrawerNavigator,
      icon: "calendar-o",
      label: t("tracker"),
    });
  }

  if (isAdminOrOwner) {
    tabScreens.push({
      name: "OperationalScans",
      component: ScansDrawerNavigator,
      icon: "steam",
      label: t("production"),
    });
  }

  useEffect(() => {
    setHasTabsToShow(tabScreens.length > 0);
  }, [userInfo]);

  return (
    <View
      style={{
        height: "100%",
        backgroundColor: colors.surface,
      }}
    >
      <Tab.Navigator
        initialRouteName={hasTabsToShow ? tabScreens[0]?.name : "Fallback"}
        screenOptions={{
          tabBarActiveTintColor: Colors[colorScheme].tint,
          tabBarInactiveTintColor: "#9ca3af",
          tabBarStyle: styles._tabBarStyle,
          tabBarLabelStyle: styles._tabBarLabelStyle,
          tabBarItemStyle: isPhone ? styles._tabBarItemStylePhone : styles._tabBarItemStyle,
          tabBarIndicatorStyle: styles._tabBarIndicatorStyle,
          swipeEnabled: false,
          lazy: true,
          lazyPlaceholder: () => (
            <View style={styles.spinnerContainer}>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={styles.spinnerStyle}
              />
            </View>
          ),
          ...(isPhone ? { tabBarScrollEnabled: true } : {}),
        }}
      >
        {tabScreens.map((tab: any) => (
          <Tab.Screen
            key={tab.name}
            name={tab.name as keyof HomeTabParamList}
            component={tab.component}
            options={{
              title: tab.label,
              tabBarIcon: ({ color }) => (
                <TabBarIcon name={tab.icon} color={color} size={20} />
              ),
            }}
          />
        ))}

        {!hasTabsToShow && (
          <Tab.Screen
            name="Fallback"
            component={FallbackScreen}
            options={{
              title: t("home"),
              tabBarIcon: ({ color }) => (
                <TabBarIcon name="home" color={color} size={20} />
              ),
            }}
          />
        )}
      </Tab.Navigator>
    </View>
  );
}

const FallbackScreen = () => (
  <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
    <TabBarIcon name="exclamation-triangle" color="gray" size={40} />
    <View style={{ marginTop: 12 }}>
      <Text>No tabs available for your role.</Text>
    </View>
  </View>
);

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    _tabBarStyle: {
      height: 60,
      width: "100%",
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      elevation: 0,
      shadowOpacity: 0,
    },
    _tabBarLabelStyle: {
      fontSize: 10,
      fontWeight: "600",
      textTransform: "none",
      textAlign: "center",
    },
    _tabBarItemStyle: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    _tabBarItemStylePhone: {
      width: "auto",
      minWidth: 70,
      paddingHorizontal: 6,
      justifyContent: "center",
      alignItems: "center",
    },
    _tabBarIndicatorStyle: {
      backgroundColor: colors.primary,
      height: 3,
      borderRadius: 3,
    },
    spinnerContainer: {
      flexDirection: "column",
      width: "100%",
      height: "100%",
      justifyContent: "center",
      alignItems: "center",
    },
    spinnerStyle: {
      flexDirection: "row",
      alignSelf: "center",
    },
  });
}
